import { useEffect } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { getSupabase } from '@/shared/db/client';

/**
 * Subscribe to Postgres change events on one or more tables and invalidate
 * the matching React Query keys whenever a row changes — so an admin's
 * status flip pops into another user's gallery instantly without
 * refetch-on-focus or polling.
 *
 * **Architecture**: this is a thin shim over Supabase Realtime. The
 * Supabase client connects with the project anon key (sets up the WS
 * connection) and our login JWT (sets the RLS role to `authenticated`).
 * RLS on each gallery table allows `select` for any authenticated session;
 * Express remains the only write path.
 *
 * **Cost model**: one `useEffect` mount per gallery, one channel per
 * table. Subscriptions are reference-counted by Supabase Realtime;
 * unmounting a gallery tears its channel down cleanly.
 *
 * **Fallback**: if `VITE_SUPABASE_URL`/`VITE_SUPABASE_ANON_KEY` aren't
 * set (dev env, smoke test) the hook is a no-op — galleries fall back
 * to React Query's normal stale-while-refetch.
 *
 * @param tables  Postgres table names to watch (e.g. ['sales_headers']).
 * @param queryKey  React Query key (or keys) to invalidate when any row
 *                  on the watched tables changes.
 */
export function useRealtimeInvalidate(
  tables: string | string[],
  queryKey: QueryKey | QueryKey[],
): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;

    const tableList = Array.isArray(tables) ? tables : [tables];
    const queryKeys: QueryKey[] = Array.isArray(queryKey[0])
      ? (queryKey as QueryKey[])
      : [queryKey as QueryKey];

    // Single channel per hook instance, multiple table subscriptions
    // attached to it — Supabase recommends this over one channel per table
    // for fewer WS round-trips.
    const channelName = `gallery:${tableList.join(',')}:${Math.random().toString(36).slice(2, 8)}`;
    const channel = sb.channel(channelName);

    const invalidateAll = () => {
      for (const key of queryKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    };

    for (const table of tableList) {
      channel.on(
        // The Supabase Realtime types here are wide on purpose; cast keeps
        // TS happy without sacrificing the runtime contract.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'postgres_changes' as any,
        { event: '*', schema: 'public', table },
        invalidateAll,
      );
    }

    // Subscribe — supabase-js auto-reconnects the underlying WebSocket
    // with exponential backoff, and re-subscribes channels on reconnect.
    // Realtime does NOT replay events that fired during the drop, so we
    // invalidate on every SUBSCRIBED to refetch the fresh state and pick
    // up anything we missed.
    let subscribed = false;
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        if (subscribed) {
          // Re-subscribe after reconnect — refetch to catch up on missed
          // events.
          invalidateAll();
        }
        subscribed = true;
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        // eslint-disable-next-line no-console
        console.warn('[realtime] channel issue, awaiting reconnect', {
          tables: tableList,
          status,
        });
        subscribed = false;
      }
    });

    // Backstop: when the device wakes from sleep / regains network, the
    // browser fires `online`. Invalidate immediately rather than wait
    // for the WS to detect the gap.
    const onOnline = () => {
      invalidateAll();
    };
    window.addEventListener('online', onOnline);

    return () => {
      window.removeEventListener('online', onOnline);
      sb.removeChannel(channel);
    };
    // tables/queryKey are expected to be stable arrays; the JSON.stringify
    // dance keeps the effect deps clean without forcing every caller to
    // memoize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tables), JSON.stringify(queryKey), queryClient]);
}
